import React, { useState, useEffect } from 'react';
import { format, addDays, parseISO } from 'date-fns';
import { servicesApi, publicApi } from '../lib/api';
import { Service, TimeSlot, AvailabilityResponse } from '../types';

type BookingStep = 'service' | 'datetime' | 'details' | 'payment' | 'confirmation';

const PublicBooking: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<BookingStep>('service');
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedService && selectedDate) {
      fetchAvailability();
    }
  }, [selectedService, selectedDate]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await servicesApi.list();
      setServices(response.data || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    if (!selectedService) return;

    setLoading(true);
    try {
      const response = await publicApi.getAvailability(
        selectedService.id,
        format(selectedDate, 'yyyy-MM-dd')
      );

      // Flatten all staff availability into a single array
      const allSlots: TimeSlot[] = [];
      Object.values(response.data as AvailabilityResponse).forEach(slots => {
        allSlots.push(...slots);
      });

      // Remove duplicates and sort
      const uniqueSlots = allSlots
        .filter((slot, index, self) =>
          index === self.findIndex(s => s.time === slot.time)
        )
        .filter(slot => slot.available)
        .sort((a, b) => a.time.localeCompare(b.time));

      setAvailableSlots(uniqueSlots);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setCurrentStep('datetime');
  };

  const handleDateTimeSelect = () => {
    if (selectedTime) {
      setCurrentStep('details');
    }
  };

  const handleDetailsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentStep('payment');
    createBooking();
  };

  const createBooking = async () => {
    if (!selectedService || !selectedTime) return;

    setLoading(true);
    try {
      const bookingData = {
        service_id: selectedService.id,
        start_time: `${format(selectedDate, 'yyyy-MM-dd')}T${selectedTime}`,
        client_attributes: {
          name: bookingDetails.name,
          email: bookingDetails.email,
          phone: bookingDetails.phone,
        },
        notes: bookingDetails.notes,
      };

      const response = await publicApi.createBooking(bookingData);

      // Redirect to Stripe Checkout if payment is required
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setCurrentStep('confirmation');
      }
    } catch (error) {
      console.error('Failed to create booking:', error);
      alert('Failed to create booking. Please try again.');
      setCurrentStep('details');
    } finally {
      setLoading(false);
    }
  };

  const getNextWeekDays = () => {
    return Array.from({ length: 7 }, (_, i) => addDays(new Date(), i));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-purple-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
            Book an Appointment
          </h1>
          <p className="text-gray-600">Choose your service and preferred time</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center gap-2">
            {['service', 'datetime', 'details', 'payment'].map((step, index) => (
              <React.Fragment key={step}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep === step
                      ? 'bg-purple-500 text-white'
                      : ['service', 'datetime', 'details', 'payment'].indexOf(currentStep) >
                        ['service', 'datetime', 'details', 'payment'].indexOf(step)
                      ? 'bg-purple-200 text-purple-700'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {index + 1}
                </div>
                {index < 3 && (
                  <div
                    className={`w-12 h-1 ${
                      ['service', 'datetime', 'details', 'payment'].indexOf(currentStep) > index
                        ? 'bg-purple-200'
                        : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Step 1: Service Selection */}
          {currentStep === 'service' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Select a Service</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {services.map(service => (
                  <button
                    key={service.id}
                    onClick={() => handleServiceSelect(service)}
                    className="p-6 border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all text-left"
                  >
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {service.name}
                    </h3>
                    {service.description && (
                      <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{service.duration_minutes} min</span>
                      <span className="text-purple-600 font-semibold">
                        ${(service.price_cents / 100).toFixed(2)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Date & Time Selection */}
          {currentStep === 'datetime' && selectedService && (
            <div>
              <button
                onClick={() => setCurrentStep('service')}
                className="mb-4 text-purple-600 hover:text-purple-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to services
              </button>

              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {selectedService.name}
              </h2>
              <p className="text-gray-600 mb-6">
                {selectedService.duration_minutes} min • ${(selectedService.price_cents / 100).toFixed(2)}
              </p>

              {/* Date Selection */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Select Date</h3>
                <div className="grid grid-cols-7 gap-2">
                  {getNextWeekDays().map(day => (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDate(day)}
                      className={`p-3 rounded-lg text-center transition-all ${
                        format(day, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd')
                          ? 'bg-purple-500 text-white shadow-md'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="text-xs font-medium">{format(day, 'EEE')}</div>
                      <div className="text-lg font-bold">{format(day, 'd')}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Select Time</h3>
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading available times...</div>
                ) : availableSlots.length > 0 ? (
                  <div className="grid grid-cols-4 gap-2">
                    {availableSlots.map(slot => (
                      <button
                        key={slot.time}
                        onClick={() => setSelectedTime(slot.time)}
                        className={`p-3 rounded-lg font-medium transition-all ${
                          selectedTime === slot.time
                            ? 'bg-purple-500 text-white shadow-md'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No available times for this date
                  </div>
                )}
              </div>

              {selectedTime && (
                <button
                  onClick={handleDateTimeSelect}
                  className="mt-6 w-full bg-purple-500 hover:bg-purple-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Continue
                </button>
              )}
            </div>
          )}

          {/* Step 3: Contact Details */}
          {currentStep === 'details' && (
            <div>
              <button
                onClick={() => setCurrentStep('datetime')}
                className="mb-4 text-purple-600 hover:text-purple-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Details</h2>

              <form onSubmit={handleDetailsSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={bookingDetails.name}
                    onChange={(e) => setBookingDetails({ ...bookingDetails, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={bookingDetails.email}
                    onChange={(e) => setBookingDetails({ ...bookingDetails, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={bookingDetails.phone}
                    onChange={(e) => setBookingDetails({ ...bookingDetails, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={bookingDetails.notes}
                    onChange={(e) => setBookingDetails({ ...bookingDetails, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Continue to Payment'}
                </button>
              </form>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {currentStep === 'confirmation' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Booking Confirmed!</h2>
              <p className="text-gray-600 mb-8">
                We've sent a confirmation email to {bookingDetails.email}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-purple-500 hover:bg-purple-600 text-white font-medium py-3 px-8 rounded-lg transition-colors"
              >
                Book Another Appointment
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicBooking;
